import { AfterViewInit, Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Question } from '../../models/underwriter.model';
import { Subject, Subscription, fromEvent } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
    selector: 'app-question-search-list',
    templateUrl: './question-search-list.component.html',
    styleUrls: ['./question-search-list.component.scss'],
    standalone: false
})
export class QuestionSearchListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() question: Question;
  @Input() readOnly = false;
  @Input() showLabel = true;
  @Input() items: any[];
  @Input() placeHolder = 'Search';
  @Input() inputType = 'text';
  @Input() value: any;
  @Input() enableServerTypeahead = false;
  @Input() minSearchTermLength = 2;
  @Input() loading = false;
  @Output() questionAnswer = new EventEmitter<any>();
  @Output() searchTermChange = new EventEmitter<string>();

  options: any[] = [];
  selectedItem: string | null = null;
  typeaheadInput$ = new Subject<string>();
  private typeaheadSub?: Subscription;
  private searchInputSub?: Subscription;
  private lastEmittedSearchTerm = '';

  @ViewChild('searchSelect') searchSelect: NgSelectComponent;

  constructor(private ngZone: NgZone) {}

  ngOnInit(): void {
    this.typeaheadSub = this.typeaheadInput$
      .pipe(
        map(term => (term || '').trim()),
        distinctUntilChanged()
      )
      .subscribe((term: string) => this.emitSearchTerm(term));
  }

  ngAfterViewInit(): void {
    const searchInput = this.searchSelect?.searchInput?.()?.nativeElement;
    if (!searchInput) {
      return;
    }

    this.searchInputSub = fromEvent(searchInput, 'input')
      .pipe(
        map((event: any) => (event?.target?.value || '').trim()),
        distinctUntilChanged()
      )
      .subscribe((term: string) => this.emitSearchTerm(term));
  }

  ngOnDestroy(): void {
    this.typeaheadSub?.unsubscribe();
    this.searchInputSub?.unsubscribe();
    this.typeaheadInput$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.question?.currentValue) {
      this.question = changes.question.currentValue;
      if (this.question?.question_prompt) {
        this.placeHolder = this.question.question_prompt;
      }
      this.selectedItem = this.question?.answer || null;
      this.getDataFromQuestionOptions();
    }
  }

  getDataFromQuestionOptions(): void {
    if (this.enableServerTypeahead) {
      return;
    }

    this.options = JSON.parse(this.question?.options || '[]');
  }

  private emitSearchTerm(term: string): void {
    if (!this.enableServerTypeahead) {
      return;
    }

    const normalized = (term || '').trim();
    if (normalized.length < this.minSearchTermLength) {
      this.lastEmittedSearchTerm = '';
      return;
    }

    if (normalized === this.lastEmittedSearchTerm) {
      return;
    }

    this.lastEmittedSearchTerm = normalized;
    this.ngZone.run(() => this.searchTermChange.emit(normalized));
  }

  submit(): void {
    if (!this.question) {
      return;
    }

    if (this.selectedItem) {
      this.question.answer = this.selectedItem;
    } else {
      this.question.answer = '';
    }

    this.questionAnswer.emit(this.question);
  }
}
